import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  input,
  computed,
} from '@angular/core';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  lucideDownload,
  lucideFileText,
  lucideImage,
  lucideBox,
} from '@ng-icons/lucide';
import { BrnDialogImports } from '@spartan-ng/brain/dialog';
import { HlmDialogImports } from '@spartan-ng/helm/dialog';
import { HlmButtonDirective } from '@spartan-ng/helm/button';
import { CampaignsService, BulkFileTag } from '@features/campaigns/services/campaigns.service';
import { ToastService } from '@shared/services/toast.service';
import { NotificationStore } from '@shared/stores/notification.store';

type DialogPhase = 'loading-tags' | 'choosing' | 'error';

@Component({
  selector: 'app-download-dialog',
  standalone: true,
  imports: [
    ...BrnDialogImports,
    ...HlmDialogImports,
    HlmButtonDirective,
    NgIconComponent,
  ],
  providers: [
    provideIcons({
      lucideDownload,
      lucideFileText,
      lucideImage,
      lucideBox,
    }),
  ],
  templateUrl: './download-dialog.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DownloadDialog {
  private readonly campaignsService = inject(CampaignsService);
  private readonly toastService = inject(ToastService);
  private readonly notificationStore = inject(NotificationStore);

  organizationId = input.required<string>();
  campaignId = input.required<string>();

  readonly dialogState = signal<'open' | 'closed'>('closed');
  readonly phase = signal<DialogPhase>('loading-tags');

  private analysisIdList: string[] = [];

  readonly availableTags = signal<BulkFileTag[]>([]);
  readonly selectedTags = signal<Set<string>>(new Set());
  readonly includeCsv = signal(true);

  readonly tags2d = computed(() => this.availableTags().filter((t) => t.type === '2d'));
  readonly tags3d = computed(() => this.availableTags().filter((t) => t.type === '3d'));
  readonly hasAnySelection = computed(() => this.includeCsv() || this.selectedTags().size > 0);

  async open(analysisIds: string[]): Promise<void> {
    this.reset();
    this.analysisIdList = analysisIds;
    this.dialogState.set('open');
    await this.loadAvailableTags();
  }

  private async loadAvailableTags(): Promise<void> {
    this.phase.set('loading-tags');
    try {
      if (this.analysisIdList.length === 0) {
        this.availableTags.set([]);
      } else {
        const result = await this.campaignsService.getBulkFilesAvailability(
          this.organizationId(),
          this.campaignId(),
          { mode: 'explicit', explicitIds: this.analysisIdList },
        );
        this.availableTags.set(result.availableTags);
        this.selectedTags.set(new Set(result.availableTags.map((t) => t.tag)));
      }
      this.phase.set('choosing');
    } catch {
      this.phase.set('error');
    }
  }

  toggleTag(tag: string): void {
    this.selectedTags.update((tags) => {
      const next = new Set(tags);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }

  toggleCsv(): void {
    this.includeCsv.update((v) => !v);
  }

  confirm(): void {
    const tagCount = this.selectedTags().size + (this.includeCsv() ? 1 : 0);
    const title = `Kampagne – ZIP (${tagCount} Dateityp${tagCount === 1 ? '' : 'en'})`;
    const filename = `campaign-${this.campaignId()}.zip`;
    const orgId = this.organizationId();
    const campaignId = this.campaignId();
    const body = {
      hubAnalysisIds: this.analysisIdList,
      fileTags: [...this.selectedTags()],
      includeCsv: this.includeCsv(),
    };

    this.notificationStore.addDownload(title, filename, () =>
      this.campaignsService.buildZipBlob(orgId, campaignId, body),
    );

    this.dialogState.set('closed');
    this.toastService.info('ZIP wird vorbereitet — Fortschritt unter Benachrichtigungen.');
  }

  onDialogClosed(): void {
    this.dialogState.set('closed');
    this.reset();
  }

  private reset(): void {
    this.phase.set('loading-tags');
    this.analysisIdList = [];
    this.availableTags.set([]);
    this.selectedTags.set(new Set());
    this.includeCsv.set(true);
  }
}
